
<template>
  <div v-if="state.init" class='flex h-full items-center justify-center'>
    <div>
      {{ $t('cart.init') }}
    </div>
  </div>

  <div v-else-if="state.order !== null" class='flex flex-col md:grid md:grid-cols-[1fr_268px] md:grid-cols-[1fr_296px] grid-rows-[auto_1fr] grid-flow-col max-w-[1184px] md:h-full mx-auto gap-x-1 lg:gap-x-4 md:py-10 pb-4 sm:pb-6 px-4 sm:px-6 xl:px-8'>
    <!-- cart_item -->
    <div class="shopping-cart pr-3">
      <!-- cart_banner -->
      <div v-if="cartBanner && isBannerActive"
           class="cart-banner py-2 "
           :style=" {
              backgroundImage: `url(${cartBanner.banner_bg})`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }">
        <div class="flex justify-center items-center lg:max-h-14">
          <img v-if="cartBanner.banner_icon"
              :src="cartBanner.banner_icon"
              alt="Banner Icon"
              class="sm:w-12 lg:w-10 ">
          <component :is="cartBanner.banner_link ? 'a' : 'div'"
                     :href="cartBanner.banner_link"
                     :target="cartBanner.banner_link ? '_blank' : null"
                     :class="['inline-block max-w-[85%]', { 'underline': cartBanner.banner_link }]">
            <div v-if="cartBannerContent"
                 v-html="cartBannerContent"
                 class="text-[14px] ml-4 lg:ml-6 mr-2">
            </div>
          </component>
        </div>
      </div>

      <h1 class="pt-4 text-24px font-medium">
        {{ $t('cart.shopping_cart') }}
      </h1>
      <div class='text-[14px] text-[#8694a2] text-medium flex flex-col sm:flex-row justify-between mt-2 mb-4'>
        <div class="flex items-center smd:block">
          {{ $t('cart.you_have') }}
          <span class='text-primary text-16px font-medium px-0.5'>
            {{ state.cart?.variants?.length || 0 }}
          </span>
          {{ $t('cart.items_in_your_shopping_cart') }}
        </div>
        <div class='continue-shopping-btn w-fit sm:w-auto inline-flex items-center btn rounded-full text-[14px] text-gray-100 mt-2 sm:mt-0 py-1.5 px-[12px] border border-gray-100 hover:bg-gray-500' @click="handleContinueShopping">
          <div class="mr-1">
            {{ $t('cart.continue_shopping') }}
          </div>
          <img src="../../images/icons/back-page.svg" class='h-4 w-4 inline-block opacity-70 rotate-180' />
        </div>
      </div>

      <div class="hidden lg:grid w-full border-b border-gray-200 px-2 text-grey-3 font-medium text-14px lg:grid-cols-[84px_84fr_54fr_18fr_38fr_80px] gap-x-3 lg:gap-x-4">
        <div>
          {{ $t('cart.item') }}
        </div>
        <div>&nbsp;</div>
        <div>
          {{ $t('cart.variant_name') }}
        </div>
        <div>&nbsp;</div>
        <div>
          {{ $t('cart.price') }}
        </div>
        <div>&nbsp;</div>
      </div>
    </div>

    <div class="relative mb-2 md:m-0 min-h-full">
      <!-- Cart Items Container -->
      <div class='md:absolute w-full overflow-y-auto md:scrollbar -mr-2 md:-mr-3 md:pr-1'
           :class="cartItemsMaxHeight"
      >
        <!-- Cart Items -->
        <template v-for="(variant, idx) in state.cart.variants">
          <div :id="`post-item-${variant.post.id}`" class="flex items-center post-widgets my-2 h-[100px] bg-grey-2 rounded-lg md:justify-between">
            <div class="w-full px-2 grid grid-cols-[84px_1fr_60px] md:grid-cols-[84px_1fr_48px] lg:grid-cols-[84px_1fr_80px] gap-x-3 lg:gap-x-4">
              <a :href="`/posts/${variant.post.id}`" class="relative">
                <img :src="variant.post.cover_image_src" class='h-[84px] w-[84px] rounded-lg inline-block max-w-none' />
                <div
                    v-if="parseFloat(variant.price) === 0"
                    class="w-10 h-4 text-center font-medium absolute free-tag pb-0.5 top-1 left-1 bg-red text-xs"
                  >
                    {{ $t('post_item.free') }}
                </div>
                <div
                    v-if="variant.phrozen_verified"
                    class="flex items-center justify-center w-10 h-4 text-center font-medium absolute pv-tag top-1 left-1 bg-red text-12px"
                  >
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-0.5">
                    <path d="M5.99941 9.57694C8.22979 9.57694 10.0379 7.76885 10.0379 5.53847C10.0379 3.30808 8.22979 1.5 5.99941 1.5C3.76902 1.5 1.96094 3.30808 1.96094 5.53847C1.96094 7.76885 3.76902 9.57694 5.99941 9.57694Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5.9982 7.15363C6.89035 7.15363 7.61359 6.43039 7.61359 5.53824C7.61359 4.64609 6.89035 3.92285 5.9982 3.92285C5.10605 3.92285 4.38281 4.64609 4.38281 5.53824C4.38281 6.43039 5.10605 7.15363 5.9982 7.15363Z" fill="white"/>
                    <path d="M8.15536 11.9996L6.00119 9.8457L3.84766 11.9996" stroke="white" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                  </svg>
                    PV
                </div>
              </a>
              <div class="grid lg:grid-cols-[34fr_31fr_15fr] gap-y-2 gap-x-3 lg:gap-x-4 font-normal text-14px">
                <div class="line-clamp-3 break-word leading-[20px] max-h-[20px]">
                  <a :href="`/posts/${variant.post.id}`" :title="variant.post.title">
                    {{ variant.post.title }}
                  </a>
                </div>
                <div class="line-clamp-3 break-word leading-[20px] max-h-[20px]">
                  {{ variant.name }}
                </div>
                <div class="flex lg:block">
                  <span>
                    <Currency :price="variant.price" />
                  </span>
                  <span v-if='variant.price < variant.original_price' class="line-through text-gray-20 font-normal ml-1 lg:ml-0">
                    <Currency :price="variant.original_price" />
                  </span>
                </div>
              </div>
              <div class="flex justify-center items-center">
                <div class="w-7 h-7 hover:bg-grey-3 rounded-full flex items-center justify-center cursor-pointer"
                     @click.prevent='deleteVariant(variant)'
                     :class="{ 'opacity-50 cursor-not-allowed': state.btn_disabled }">
                  <div class="destroy-post w-5 h-5 inline-block pointer-events-none"></div>
                </div>
              </div>
            </div>
          </div>

          <hr class='border-gray-500'>
        </template>
      </div>

      <!-- wishlist_recommendations_desktop -->
      <div
        v-if="state.wishlist_items.length > 0"
        class="wishlist-recommendations hidden md:block w-full bg-bg-base absolute bottom-[12px] lg:bottom-[-36px] left-0 z-10 smd:pr-2 md:pr-3"
        data-controller="wishlist-slider"
        :data-wishlist-slider-refresh-value="state.wishlist_items.length"
      >
        <!-- Header with Navigation -->
        <div class="flex flex-col lg:flex-row lg:items-center justify-between mt-2 mb-4">
          <div>
            <span class="text-14px">{{ $t('cart.wishlist_calling') }}</span>
            <span class="text-12px text-gray-150"> — {{ $t('cart.wishlist_pick') }}</span>
          </div>
          <a v-if="currentUser" :href="getWishlistUrl()" class="text-12px text-gray-100 pt-2 lg:pt-0 hover:underline transition-colors">
            <img src="../../images/icons/save.svg" class="w-4 h-4 inline-block mr-1 opacity-70">
            {{ $t('cart.wishlist') }} ({{ state.wishlist_items.length > 10 ? '10+' : state.wishlist_items.length }})
          </a>
        </div>

        <!-- Swiper Container -->
        <div class="relative w-full overflow-x-hidden overflow-y-visible">
          <!-- Navigation Buttons -->
          <button
            data-wishlist-slider-target="swiperPrev"
            class= "absolute top-[42px] left-4 z-10 w-8 h-8 rounded-full border-[1.6px] bg-white/10 backdrop-blur-sm hover:bg-[#6a6a6a] flex items-center justify-center transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <button
            data-wishlist-slider-target="swiperNext"
            class="absolute top-[42px] right-4 z-10 w-8 h-8 rounded-full border-[1.6px] bg-white/10 backdrop-blur-sm hover:bg-[#6a6a6a] flex items-center justify-center transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="swiper w-full h-[190px]" data-wishlist-slider-target="swiper">
            <div class="swiper-wrapper">
            <div
              v-for="item in state.wishlist_items"
              :key="item.id"
              class="swiper-slide !w-[116px] opacity-60 hover:opacity-100 transition-opacity duration-300"
              style="width: 116px; flex-shrink: 0;"
            >
              <div
                class="wishlist-item relative cursor-pointer w-full"
                :data-wishlist-item-id="item.id"
                @mouseenter="handleWishlistItemHover(item.id, true)"
                @mouseleave="handleWishlistItemHover(item.id, false)"
              >
                <div class="relative">
                  <img
                    :src="item.cover_image_src"
                    :alt="item.title"
                    class="w-full h-full object-cover rounded-lg"
                  />

                  <!-- Hover Overlay -->
                  <div
                    v-if="wishlist_ui_state[item.id]?.hovering"
                    class="absolute inset-0 rounded-lg flex flex-col justify-center items-center text-white"
                  >
                    <!-- Cart Button -->
                    <button
                    class="text-white p-1 rounded-full transition-colors hover:bg-gray-200"
                      :disabled="state.btn_disabled"
                      @click.stop="handleWishlistItemClick(item)"
                    >
                      <img src="../../images/icons/cart.svg" class="h-8 w-8">
                    </button>
                  </div>
                </div>
                <div
                  v-if="item.variants && item.variants.length > 1 && wishlist_ui_state[item.id]?.showDropdown"
                  class="absolute top-full left-0 w-[116px] max-w-[116px] max-h-[72px] bg-gray-500 rounded-lg shadow-lg z-50 overflow-y-auto smd:scrollbar"
                  style="box-shadow: 0px 4px 4px 0px #00000080;"
                  @click.stop
                >
                  <template v-for="(variant, index) in item.variants" :key="variant.id">
                    <div
                      class="pl-1 py-1 cursor-pointer text-12px"
                      @click="handleWishlistVariantClick(variant.id)"
                    >
                      <div class="font-medium truncate pl-2 pr-1 py-1 hover:bg-gray-400 rounded-lg">
                      {{ variant.name || 'Unknown Variant' }}
                      </div>
                    </div>
                    <hr v-if="index < item.variants.length - 1"
                        class="border-grey-3 mx-2">
                  </template>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- wishlist_recommendations_mobile -->
    <div
        v-if="state.wishlist_items.length > 0"
        class="wishlist-recommendations block md:hidden w-full"
        data-controller="wishlist-slider"
        :data-wishlist-slider-refresh-value="state.wishlist_items.length"
      >
        <!-- Header with Navigation -->
        <div class="flex flex-col sm:flex-row sm:items-center justify-between mt-2 mb-4 leading-[16px]">
          <div>
            <span class="text-14px">{{ $t('cart.wishlist_calling') }}</span>
            <span class="text-12px text-gray-150"> — {{ $t('cart.wishlist_pick') }}</span>
          </div>
          <a v-if="currentUser" :href="getWishlistUrl()" class="text-12px text-gray-100 pt-2 hover:underline transition-colors">
            <img src="../../images/icons/save.svg" class="w-4 h-4 inline-block mr-1 opacity-70">
            {{ $t('cart.wishlist') }} ({{ state.wishlist_items.length > 10 ? '10+' : state.wishlist_items.length }})
          </a>
        </div>

        <!-- Swiper Container -->
        <div class="relative w-full overflow-x-hidden overflow-y-visible">
          <!-- Navigation Buttons -->
          <button
            data-wishlist-slider-target="swiperPrev"
            class= "absolute top-[42px] left-4 z-10 w-8 h-8 rounded-full border-[1.6px] bg-white/10 backdrop-blur-sm hover:bg-[#6a6a6a] flex items-center justify-center transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <button
            data-wishlist-slider-target="swiperNext"
            class="absolute top-[42px] right-4 z-10 w-8 h-8 rounded-full border-[1.6px] bg-white/10 backdrop-blur-sm hover:bg-[#6a6a6a] flex items-center justify-center transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="swiper w-full h-[190px]" data-wishlist-slider-target="swiper">
            <div class="swiper-wrapper">
            <div
              v-for="item in state.wishlist_items"
              :key="item.id"
              class="swiper-slide !w-[116px] opacity-60 hover:opacity-100 transition-opacity duration-300"
              style="width: 116px; flex-shrink: 0;"
            >
              <div
                class="wishlist-item relative cursor-pointer w-full"
                :data-wishlist-item-id="item.id"
                @mouseenter="handleWishlistItemHover(item.id, true)"
                @mouseleave="handleWishlistItemHover(item.id, false)"
              >
                <div class="relative">
                  <img
                    :src="item.cover_image_src"
                    :alt="item.title"
                    class="w-full h-full object-cover rounded-lg"
                  />

                  <!-- Hover Overlay -->
                  <div
                    v-if="wishlist_ui_state[item.id]?.hovering"
                    class="absolute inset-0 rounded-lg flex flex-col justify-center items-center text-white"
                  >
                    <!-- Cart Button -->
                    <button
                      class="text-white p-1.5 rounded-full transition-colors hover:bg-gray-200"
                      :disabled="state.btn_disabled"
                      @click.stop="handleWishlistItemClick(item)"
                    >
                      <img src="../../images/icons/cart.svg" class="h-8 w-8">
                    </button>
                  </div>
                </div>

                <!-- Absolute Positioned Dropdown -->
                <div
                  v-if="item.variants && item.variants.length > 1 && wishlist_ui_state[item.id]?.showDropdown"
                  class="absolute top-full left-0 w-[116px] max-w-[116px] max-h-[72px] bg-gray-500 rounded-lg shadow-lg z-50 overflow-y-auto scrollbar"
                  style="box-shadow: 0px 4px 4px 0px #00000080;"
                  @click.stop
                >
                  <template v-for="(variant, index) in item.variants" :key="variant.id">
                    <div
                      class="px-1 py-1 cursor-pointer text-12px"
                      @click="handleWishlistVariantClick(variant.id)"
                    >
                      <div class="font-medium truncate px-2 py-1 hover:bg-gray-400 rounded-lg">
                        {{ variant.name || 'Unknown Variant' }}
                      </div>
                    </div>
                    <hr v-if="index < item.variants.length - 1"
                        class="border-grey-3 mx-2">
                  </template>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
    </div>
    <!-- order_summary -->
    <div class="row-span-2">
      <div class="p-2 bg-grey-7 rounded-xl" :class="{'mt-[-48px]': state.wishlist_items.length > 1, 'md:mt-0': true}">
        <div class='m-2 mb-4 text-18px font-medium'>
          {{ $t('cart.order_summary') }}
        </div>

        <div class="py-2 px-4 bg-[#2E3134] rounded-8px">
          <!-- promotion -->
          <div id="cart-promotion" :key="state.in_used_promotion_items[0]?.code">
            <input id='discount-type-promotion' type='radio' name='discount-type' value='promotion' :checked="discount_state.collapse_type == 'promotion'">
            <div class="text-14px font-medium mb-1">
              {{ $t('cart.promotion') }}
            </div>
            <template v-if="state.in_used_promotion_items.length == 0">
              <form class="edit_cart">
                <div class="relative mb-2">
                  <div :class="discount_state.promotion_error == '' ? 'relative' : 'relative error'">
                    <input id='promotion-code-input' type="text" name='promotion_code' class='w-full default-input-klass !border-gray-150'
                           :placeholder="$t('cart.enter_promotion')"
                           :value="discount_state.selected_promotion"
                           @keyup='typePromotionCode'>
                  </div>
                  <div class='collapse-btn' @click="changeDiscountCollapseType('promotion')"></div>
                </div>

                <div v-if="state.available_promotions.length > 0" class="items-content overflow-y-auto overflow-x-hidden smd:scrollbar smd:-mr-3 smd:pr-1">
                  <div class="flex flex-col gap-y-2">
                    <template v-for="promotion in state.available_promotions">
                      <div :class="'discount-item p-2 border border-grey-1 rounded-8px ' + selectedPromotionStyle(promotion.code)"
                           @click="selectPromotion(promotion.code)">
                        <div class="item-price min-w-[78px]">
                          <CurrencyAdvanced :price="promotion.discount_price" :integer-size="32" :decimal-size="16" :symbol-size="16" :symbol-break-line="true" />
                        </div>
                        <div class="md:max-w-[116px]">
                          <div class="item-code text-12px text-gray-150 break-all font-medium truncate">
                            {{ promotion.code }}
                          </div>
                          <div class="item-expires text-12px text-grey-5">
                            {{ $t('cart.discount_item.expires') }}
                            <span class="ml-2 font-medium">
                              <IsoToUTC :isoDate="promotion.end_at" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>

                <div v-if="discount_state.promotion_error != ''" class="error-msg text-12px text-danger -mt-1 mb-1">
                  {{ discount_state.promotion_error }}
                </div>

                <button class="w-full btn btn-sm btn-primary"
                  :disabled="discount_state.selected_promotion == '' || state.btn_disabled"
                  @click.prevent="submitPromotion">
                  {{ $t('cart.discount_item.apply') }}
                </button>
              </form>
            </template>
            <template v-else>
              <div v-for="promotion_item in state.in_used_promotion_items" class="flex items-center text-14px font-medium text-gray-10">
                <div class="flex-1 truncate" :title="promotion_item.source.code">
                  {{ promotion_item.source.code }}
                </div>
                <div class="mx-1">
                  <Currency :price="promotion_item.price" />
                </div>
                <button class='cursor-pointer'
                  :disabled="state.btn_disabled"
                  @click='deletePromotion(promotion_item.source.code)'>
                  <img src="../../images/icons/cancel-select-coupon.svg" class='h-6 w-6 inline-block mb-[2px]' />
                </button>
              </div>
            </template>
          </div>
          <!-- coupon -->
          <div id="cart-coupon" v-if="state.available_coupons.length > 0 || state.in_used_coupon_items.length > 0" class='mt-6' :key="state.in_used_coupon_items.map(obj => obj.source.code).join(', ')">
            <input id='discount-type-coupon' type='radio' name='discount-type' value='coupon' :checked="discount_state.collapse_type == 'coupon'">
            <div class="text-16px font-medium mb-2">
              {{ $t('cart.coupon') }}
            </div>
            <div id='selected-coupon' class="mb-2">
              <div v-for="coupon_item in state.in_used_coupon_items" class="flex items-center text-14px font-medium text-gray-10">
                <div class="flex-1 truncate" :title="coupon_item.source.code">
                  {{ coupon_item.source.code }}
                </div>
                <div class="mx-1">
                  <Currency :price="coupon_item.price" />
                </div>
                <button class='cursor-pointer'
                  :disabled="state.btn_disabled"
                  @click='deleteCoupon(coupon_item.source.code)'>
                  <img src="../../images/icons/cancel-select-coupon.svg" class='h-6 w-6 inline-block mb-[2px]' />
                </button>
              </div>
            </div>

            <form class="edit_cart">
              <div class="items-content overflow-y-auto smd:scrollbar smd:-mr-3 smd:pr-1">
                <div class="flex flex-col gap-y-2">
                  <template v-for="coupon in state.available_coupons">
                    <div :class="'discount-item p-2 border border-grey-1 rounded-8px ' + selectedCouponStyle(coupon.code)"
                         @click="selectCoupon(coupon.code)">
                      <div class="item-price min-w-[78px]">
                        <CurrencyAdvanced :price="coupon.discount_price" :integer-size="32" :decimal-size="16" :symbol-size="16" :symbol-break-line="true" />
                      </div>
                      <div class="md:max-w-[116px]">
                        <div class="item-code text-12px text-gray-150 break-all font-medium truncate">
                          {{ coupon.code }}
                        </div>
                        <div class="item-expires text-12px text-grey-5">
                          {{ $t('cart.discount_item.expires') }}
                          <span class="ml-2 font-medium">
                            <IsoToUTC :isoDate="coupon.end_at" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <div v-if="discount_state.coupon_error != ''" class="error-msg text-12px text-danger -mt-1 mb-1">
                {{ discount_state.coupon_error }}
              </div>

              <div v-if="discount_state.collapse_type == 'coupon'" class="btns flex gap-2">
                <div class="flex-1 btn btn-sm btn-secondary" @click="changeDiscountCollapseType('coupon')">
                  {{ $t('cart.discount_item.cancel') }}
                </div>
                <button class="flex-1 btn btn-sm btn-primary"
                  :disabled="discount_state.selected_coupons.length == 0 || state.btn_disabled"
                  @click.prevent="submitCoupon">
                  {{ $t('cart.discount_item.apply') }}
                </button>
              </div>
              <div v-else-if="state.available_coupons.length == 0" class="coupon-collaspe-btn btn btn-sm btn-primary disabled">
                {{ $t('cart.discount_item.choose') }}
              </div>
              <div v-else class="coupon-collaspe-btn btn btn-sm btn-primary" @click="changeDiscountCollapseType('coupon')">
                {{ $t('cart.discount_item.choose') }}
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="mt-4 p-4 bg-grey-7 rounded-xl">
        <div class='flex flex-col gap-y-1 text-16px font-medium'>
          <div id='cart-subtotal' class='flex justify-between'>
            <div class="text">
              {{ $t('cart.subtotal') }}
            </div>
            <div class="price">
              <Currency :price="state.order.order_price" />
            </div>
          </div>
          <div id='cart-discount'>
            <div class='flex justify-between'>
              <div class="text">
                {{ $t('cart.discount') }}
              </div>
              <div class="price">
                <Currency :price="state.order.discount_price" />
              </div>
            </div>
            <div v-if="state.order.coin_price !== 0" id='cart-coin' class='flex justify-between text-12px font-thin text-gray-150'>
              <div class="text">
                {{ $t('cart.coin') }}
                <span v-if="state.order.coin_amount > 0">{{ $t('cart.coin_amount', { coins: formattedCoinAmount }) }}</span>
              </div>
              <div class="price flex items-baseline">
                <Currency :price="Math.abs(state.order.coin_price)" />
              </div>
            </div>
            <!-- 為了最低收款額少抵的積分要講原因，否則買家會以為積分沒有全部抵掉是壞了。
                 積分剛好全部退回時上面那列不會出現，這列要獨立判斷 -->
            <div v-if="state.order.coin_held_amount > 0" id='cart-coin-held' class='text-12px font-thin text-gray-150'>
              {{ $t('cart.coin_held', { amount: state.order.minimum_charge, coins: formattedCoinHeldAmount }) }}
            </div>
          </div>
        </div>
        <hr class='border-gray-200 my-4'>
        <div class='flex flex-col gap-y-4'>
          <!-- 刻意多包一層：讓預估回饋緊貼總價下方，不要吃到父層 gap-y-4 的間距 -->
          <div>
            <div id='cart-total-price' class='flex justify-between text-20px font-medium'>
              <div class="text">
                {{ $t('cart.total_price') }}
              </div>
              <div class="price">
                <Currency :price="state.order.final_price" />
              </div>
            </div>

            <!-- text-warning 是 Pixup Points 的共用色：My Point 餘額卡、navbar chip、
                 作品頁餘額提示都是它。這一列講的也是點數，跟著同一個色票走，
                 使用者才認得出「這是點數，不是折扣金額」。 -->
            <div v-if="state.estimated_reward > 0" id='cart-estimated-reward' class='flex justify-between text-12px font-thin text-warning'>
              <div class="text">
                {{ $t('cart.estimated_reward') }}
              </div>
              <div class="price">
                {{ formattedEstimatedReward }} {{ $t('cart.estimated_reward_unit') }}
              </div>
            </div>
          </div>

          <form @submit.prevent="checkout">
            <input v-if="state.btn_disabled" type="submit" class="w-full h-auto text-16px btn stripe_gw_btn hover:bg-[#7e7aff]"
                   :value="$t('cart.pay_with_gateway', { gateway: state.cart.gateway_provider })" disabled>
            <input v-else type="submit" class="w-full h-auto text-16px btn stripe_gw_btn hover:bg-[#7e7aff]"
                   :value="$t('cart.pay_with_gateway', { gateway: state.cart.gateway_provider })"
                   :data-disable-with="$t('cart.redirect_to_gateway', { gateway: state.cart.gateway_provider })">
          </form>

          <div class="flex justify-center items-center">
            <img src="../../images/alert-warning.svg" class='h-6 w-6 mr-2' />
            <div class="text-[12px] text-[#FFCC6C]">
              {{ $t('cart.purchase_reminder') }}
              <a class="text-[#FFCC6C] underline" href="/term_service">
                {{ $t("designers.application_form.terms_of_service") }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="flex flex-col justify-center items-center h-full xs:py-10 md:py-30  xs:mx-2 md:mx-20">
    <p class='font-medium xs:text-24px sm:text-32px'>
      {{ $t('cart.empty') }}
    </p>
    <p class='xs:w-[234px] sm:w-auto text-center mt-1.5 font-normal text-gray-10 text-16px'>
      {{ $t('cart.empty_content') }}
    </p>
    <img src="../../images/cart-empty.svg" class='inline-block max-w-[84px] max-h-[84px] mb-8 mt-6' />
    <div class='continue-shopping-btn w-fit sm:w-auto inline-flex items-center btn rounded-full text-[14px] text-gray-100 mt-2 sm:mt-0 py-1.5 px-[12px] border border-gray-100 hover:bg-gray-500' @click="handleContinueShopping">
      <div class="mr-1">
        {{ $t('cart.continue_shopping') }}
      </div>
      <img src="../../images/icons/back-page.svg" class='h-4 w-4 inline-block opacity-70 rotate-180' />
    </div>
  </div>

</template>

<script>

import { defineComponent, ref, reactive, computed, onMounted, onUpdated, onBeforeUnmount, nextTick } from "vue";
import { getUrlLocale } from '../utils/getUrlLocale';

const COUPON_TYPE = Object.freeze({ PROMOTION: 'promotion', COUPON: 'coupon' })

export default defineComponent({
  setup(props) {
    const currentUser = ref(null);
    const state = reactive({
      init: true,
      loading: false,
      btn_disabled: false,
      cart: null,
      order: null,
      available_promotions: [],
      available_coupons: [],
      estimated_reward: 0,
      in_used_promotion_items: [],
      in_used_coupon_items: [],
      wishlist_items: []
    });

    const discount_state = reactive({
      collapse_type: 'promotion',
      selected_promotion: '',
      promotion_error: '',
      selected_coupons: [],
      coupon_error: ''
    });

    const wishlist_ui_state = reactive({});
    const locale = ref('');

    const cartBanner = ref(null);

    // estimated_reward is a Pixup Points (coin) amount, not a currency amount —
    // never format it with the Currency/CurrencyAdvanced components.
    const formattedEstimatedReward = computed(() => state.estimated_reward.toLocaleString());
    // coin_amount 同樣是點數（後端換算好的），不要自己從 coin_price 乘回來
    const formattedCoinAmount = computed(() => (state.order.coin_amount || 0).toLocaleString());
    const formattedCoinHeldAmount = computed(() => (state.order.coin_held_amount || 0).toLocaleString());

    const isBannerActive = computed(() => {
      if (!cartBanner.value) return false;

      const now = new Date();
      const startDate = new Date(cartBanner.value.start_at);
      const endDate = new Date(cartBanner.value.end_at);

      return now >= startDate && now <= endDate;
    });

    const cartBannerContent = computed(() => {
      return cartBanner.value?.content || '';
    });

    const cartItemsMaxHeight = computed(() => {
      const hasWishlist = state.wishlist_items.length > 0;
      const hasExpandedDiscount = discount_state.collapse_type === 'promotion' || discount_state.collapse_type === 'coupon';

      if (hasWishlist) {
        // 當有 wishlist 推薦時，需要為 wishlist 預留空間
        if (hasExpandedDiscount) {
          // 當 discount 展開時，減少更多高度以避免被 footer 遮住
          return 'max-h-[calc(100%-280px)] lg:max-h-[calc(100%-208px)]';
        } else {
          // 當 discount 收起時，使用原本的高度
          return 'max-h-[calc(100%-280px)] lg:max-h-[calc(100%-200px)]';
        }
      } else {
        // 當沒有 wishlist 推薦時
        if (hasExpandedDiscount) {
          // 當 discount 展開時，使用較小的固定高度
          return 'md:max-h-[calc(100%)]';
        } else {
          // 當 discount 收起時，使用原本的固定高度
          return 'md:max-h-[calc(100%)]';
        }
      }
    });

    const getWishlistUrl = () => {
      if (!currentUser.value) return '#';
      return `/${locale.value}/p/${currentUser.value.username}/saved_posts`;
    };

    const fetchCurrentUser = async () => {
      try {
        const response = await fetch('/api/v1/profile/current_user', {
          method: 'GET',
          credentials: 'include',
        });
        if (response.ok) {
          currentUser.value = await response.json();
        } else {
          throw new Error('Failed to fetch current user');
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    const fetchCartData = async () => {
      if (state.loading) return;

      state.loading = true;

      try {
        const response = await fetch('/api/v1/cart/current_post_cart');

        if (response.ok) {
          const data = await response.json();

          // 收舊單時發現其實已付款：後端只回導頁網址，不回購物車資料
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
            return;
          }

          // 初始 in_used_discount
          state.in_used_promotion_items = []
          state.in_used_coupon_items = []

          state.cart = data.cart;
          state.order = data.order;
          state.available_promotions = data.available_promotions;
          state.available_coupons = data.available_coupons;
          state.estimated_reward = data.estimated_reward;

          if (state.init && state.available_promotions.length > 0) {
            discount_state.selected_promotion = state.available_promotions[0].code
          }

          data.order?.line_items.forEach(line_item => {
            if (line_item.source_type == 'Promotion') {
              state.in_used_promotion_items.push(line_item)
            } else if (line_item.source_type == 'Coupon') {
              state.in_used_coupon_items.push(line_item)
            }
          });
        }
      } catch (error) {
        console.error('Error fetching model data:', error);
      } finally {
        state.init = false;
        state.loading = false;
      }
    };

    const fetchCartBanner = async () => {
      try {
        const response = await fetch('/api/v1/cart/banner');

        if (response.ok) {
          const data = await response.json();
          cartBanner.value = data.banner;
        }
      } catch (error) {
        console.error('Error fetching model data:', error);
      }
    };

    const fetchWishlistRecommendations = async () => {
      try {
        const response = await fetch('/api/v1/cart/wishlist_recommendations');

        if (response.ok) {
          const data = await response.json();
          state.wishlist_items = data.wishlist_items || [];

          // 觸發 Swiper 重新初始化
          nextTick(() => {
            const wishlistController = document.querySelector('[data-controller="wishlist-slider"]');
            if (wishlistController && wishlistController.application) {
              const controller = wishlistController.application.getControllerForElementAndIdentifier(
                wishlistController,
                'wishlist-slider'
              );
              if (controller && controller.refresh) {
                controller.refresh();
              }
            }
          });
        }
      } catch (error) {
        console.error('Error fetching wishlist recommendations:', error);
        state.wishlist_items = [];
      }
    };

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleString();
    };

    const deleteVariant = async (variant) => {
      if (state.btn_disabled) return;

      state.btn_disabled = true;

      try {
        // tracking
        const eventData = formatTrackingData(variant)
        trackingEvent.remove_from_cart(eventData)

        const response = await fetch(`/api/v1/cart/variants/${variant.id}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          await fetchCartData()

          // update cart icon number
          let evt = new CustomEvent('addToCart', { 'detail': { 'cart_item_count': state.cart.variants.length } });
          document.dispatchEvent(evt);
        } else {
          throw new Error('Failed to delete variant');
        }
      } catch (error) {
        console.error('Error deleting variant:', error);
      } finally {
        state.btn_disabled = false
      }
    };

    const changeDiscountCollapseType = (value) => {
      discount_state.promotion_error = ''
      discount_state.coupon_error = ''

      if (discount_state.collapse_type == value) {
        discount_state.collapse_type = ''
        discount_state.selected_promotion = ''
        discount_state.selected_coupons = []
      } else {
        discount_state.collapse_type = value
      }
    };

    const typePromotionCode = (event) => {
      discount_state.selected_promotion = event.target.value
      discount_state.promotion_error = ''
      discount_state.coupon_error = ''
    };

    const selectPromotion = (code) => {
      discount_state.selected_promotion = code
      discount_state.promotion_error = ''
      discount_state.coupon_error = ''
    };

    const selectedPromotionStyle = (code) => {
      if (discount_state.selected_promotion == code) {
        return 'selected';
      } else {
        return '';
      }
    };

    const submitPromotion = async () => {
      if (state.btn_disabled) return;

      state.btn_disabled = true;

      discount_state.promotion_error = ''
      discount_state.coupon_error = ''

      try {
        const formData = new FormData()
        formData.append('code', discount_state.selected_promotion)

        const response = await fetch('/api/v1/cart/promotions', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          // GA4: apply_coupon (promotion)
          const selectedPromo = state.available_promotions.find(p => p.code === discount_state.selected_promotion)
          trackApplyCoupon(COUPON_TYPE.PROMOTION, discount_state.selected_promotion, selectedPromo?.discount_price)
          await fetchCartData()
        } else {
          const errorData = await response.json();
          discount_state.promotion_error = errorData.error
        }
      } catch (error) {
        console.error('Error submiting promotion:', error);
      } finally {
        state.btn_disabled = false
      }
    };

    const deletePromotion = async (code) => {
      if (state.btn_disabled) return;

      state.btn_disabled = true;

      discount_state.promotion_error = ''
      discount_state.coupon_error = ''

      try {
        const response = await fetch(`/api/v1/cart/promotions/${code}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          await fetchCartData()
        } else {
          const errorData = await response.json();
          discount_state.promotion_error = errorData.error;
        }
      } catch (error) {
        console.error('Error deleting promotion:', error);
      } finally {
        state.btn_disabled = false
      }
    };

    const selectCoupon = (code) => {
      let coupons = discount_state.selected_coupons

      if (coupons.indexOf(code) > -1) {
        discount_state.selected_coupons = coupons.filter(function(e) { return e !== code })
      } else {
        discount_state.selected_coupons.push(code)
      }

      discount_state.promotion_error = ''
      discount_state.coupon_error = ''
    };

    const selectedCouponStyle = (code) => {
      if (discount_state.selected_coupons.indexOf(code) > -1) {
        return 'selected';
      } else {
        return '';
      }
    };

    const submitCoupon = async () => {
      if (state.btn_disabled) return;

      state.btn_disabled = true;

      discount_state.promotion_error = ''
      discount_state.coupon_error = ''

      try {
        const formData = new FormData()
        discount_state.selected_coupons.forEach(function(code) {
          formData.append('codes[]', code)
        })

        const response = await fetch('/api/v1/cart/coupons', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          // GA4: apply_coupon (coupon)
          discount_state.selected_coupons.forEach(code => {
            const selectedCoupon = state.available_coupons.find(c => c.code === code)
            trackApplyCoupon(COUPON_TYPE.COUPON, code, selectedCoupon?.discount_price)
          })
          await fetchCartData()
        } else {
          const errorData = await response.json();
          discount_state.coupon_error = errorData.error
        }
      } catch (error) {
        console.error('Error submiting coupon:', error);
      } finally {
        state.btn_disabled = false
      }
    };

    const deleteCoupon = async (code) => {
      if (state.btn_disabled) return;

      state.btn_disabled = true;

      try {
        const response = await fetch(`/api/v1/cart/coupons/${code}`, {
          method: 'DELETE',
        });
        if (response.ok) {
          await fetchCartData()
        } else {
          throw new Error('Failed to delete coupon');
        }
      } catch (error) {
        console.error('Error deleting coupon:', error);
      } finally {
        state.btn_disabled = false
      }
    };

    const checkout = () => {
      state.btn_disabled = true;

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = '/cart/checkout';
      form.style.display = 'none';

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'authenticity_token';
      input.value = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
      form.appendChild(input);

      document.body.appendChild(form);
      form.submit();
    };

    const handleContinueShopping = () => {
      const currentHost = window.location.host;
      const referrer = document.referrer;

      if (referrer && referrer !== '' && referrer.includes(currentHost)) {
        window.location.href = referrer;
      } else {
        window.location.href = '/posts';
      }
    };

    const formatTrackingData = (variant) => {
      return {
        'id': variant.id,
        'title': `${variant.post.title}_${variant.name}`,
        'price': variant.price,
        'original_price': variant.original_price,
        'designer': variant.post.creator.brand_name
      }
    }

    const trackApplyCoupon = (type, code, discountValue) => {
      if (!window.trackingEvent) return
      window.trackingEvent.apply_coupon({
        coupon_type: type,
        coupon_code: code,
        discount_value: discountValue,
      })
    }

    const addVariantToCart = async (variantId) => {
      if (state.btn_disabled) return;
      state.btn_disabled = true;

      try {
        const response = await fetch(`/api/v1/cart/variants/${variantId}`, {
          method: 'POST',
        });

        if (response.ok) {
          const data = await response.json();

          // 更新購物車資料和 wishlist 推薦
          await fetchCartData();
          await fetchWishlistRecommendations();

          // 更新購物車圖標數量
          let evt = new CustomEvent('addToCart', { 'detail': { 'cart_item_count': data.cart_item_count } });
          document.dispatchEvent(evt);

          // 關閉所有 wishlist dropdown
          Object.keys(wishlist_ui_state).forEach(itemId => {
            if (wishlist_ui_state[itemId]?.showDropdown) {
              wishlist_ui_state[itemId].showDropdown = false;
            }
          });
        } else {
          throw new Error('Failed to add variant to cart');
        }
      } catch (error) {
        console.error('Error adding variant to cart:', error);
      } finally {
        state.btn_disabled = false;
      }
    };


    const handleWishlistItemClick = (item) => {
      if (state.btn_disabled) return;

      if (item.variants && item.variants.length > 1) {
        // Toggle dropdown for multi-variant items
        const itemId = item.id;

        // 確保狀態初始化
        if (!wishlist_ui_state[itemId]) {
          wishlist_ui_state[itemId] = {
            hovering: false,
            showDropdown: false
          };
        }

        // 簡單切換下拉選單
        wishlist_ui_state[itemId].showDropdown = !wishlist_ui_state[itemId].showDropdown;
      } else {
        // Single variant - add directly to cart
        if (item.variants && item.variants.length > 0) {
          const defaultVariant = item.variants[0];
          addVariantToCart(defaultVariant.id);
        }
      }
    };

    const handleWishlistVariantClick = (variantId) => {
      addVariantToCart(variantId);
      // 選擇後關閉所有下拉選單
      Object.keys(wishlist_ui_state).forEach(itemId => {
        if (wishlist_ui_state[itemId]?.showDropdown) {
          wishlist_ui_state[itemId].showDropdown = false;
        }
      });
    };


    // 點擊外部關閉下拉選單
    const handleClickOutside = (event) => {
      const isClickInsideWishlistItem = event.target.closest('[data-wishlist-item-id]');

      if (!isClickInsideWishlistItem) {
        // 關閉所有下拉選單
        Object.keys(wishlist_ui_state).forEach(itemId => {
          if (wishlist_ui_state[itemId]?.showDropdown) {
            wishlist_ui_state[itemId].showDropdown = false;
          }
        });
      }
    };

    const handleWishlistItemHover = (itemId, isHovering) => {
      if (!wishlist_ui_state[itemId]) {
        wishlist_ui_state[itemId] = {
          hovering: false,
          showDropdown: false
        };
      }

      if (isHovering) {
        wishlist_ui_state[itemId].hovering = true;
      } else {
        // 只有在下拉選單未顯示時才隱藏 hover 狀態
        if (!wishlist_ui_state[itemId].showDropdown) {
          wishlist_ui_state[itemId].hovering = false;
        }
      }
    };

    onMounted(async () => {
      // 添加點擊外部關閉下拉選單的事件監聽器
      document.addEventListener('click', handleClickOutside);

      // 初始化 locale
      locale.value = getUrlLocale();

      try {
        await fetchCurrentUser();

        if (currentUser.value) {
          await Promise.all([
            fetchCartData(),
            fetchCartBanner(),
            fetchWishlistRecommendations()
          ]);
        } else {
          console.error('Failed to fetch current user');
        }
      } catch (error) {
        console.error('Error in onMounted:', error);
      } finally {
        // tracking
        if (state.order) {
          const eventData = {
            finalPrice: state.order.final_price,
            items: []
          }

          state.cart.variants.forEach(variant => {
            const variantData = formatTrackingData(variant)
            eventData.items.push(variantData)
          })
          trackingEvent.view_cart(eventData)
        }
      }
    });


    return {
      currentUser,
      state,
      cartBanner,
      cartBannerContent,
      cartItemsMaxHeight,
      discount_state,
      wishlist_ui_state,
      isBannerActive,
      formattedEstimatedReward,
      formattedCoinAmount,
      formattedCoinHeldAmount,
      getWishlistUrl,
      deleteVariant,
      changeDiscountCollapseType,
      typePromotionCode,
      selectPromotion,
      selectedPromotionStyle,
      submitPromotion,
      deletePromotion,
      selectCoupon,
      selectedCouponStyle,
      submitCoupon,
      deleteCoupon,
      checkout,
      formatDate,
      handleContinueShopping,
      addVariantToCart,
      handleWishlistItemClick,
      handleWishlistVariantClick,
      handleWishlistItemHover
    };
  }
});
</script>
